import { MoreVert, CheckCircleRounded, DeleteOutlineRounded, DownloadForOfflineRounded, UndoRounded, OpenInBrowserRounded } from '@mui/icons-material'
import { Box, Button, Grid, Menu, MenuItem } from '@mui/material'
import { LargeCard } from 'Components/Cards/Large'
import { FormatDateTimeValue, FormatDateValue, FormatCurrency } from 'core/Utilities'
import { useState } from 'react'
import { type DocumentData } from '../../core/types'

interface DocumentDisplayProps {
  document: DocumentData
  canVerify: boolean
  canEdit: boolean
  canDelete?: boolean
  onDelete: () => void
  onVerifyChanged: (verified: boolean) => void
  onDownload?: (id: number) => void
  deleteOptionText?: string
}

export function DocumentDisplay (props: DocumentDisplayProps): JSX.Element {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [showVerify, setShowVerify] = useState(true)

  const fileExtenstion = props.document.fileName.split('.').pop()?.toLowerCase()
  // the extensions that can be opened in the browser
  const canOpen = fileExtenstion === 'pdf' || fileExtenstion === 'jpg' || fileExtenstion === 'jpeg' || fileExtenstion === 'png'

  const noReturn = (): void => { }

  const openDocument = (): void => {
    const url = `${process.env.REACT_APP_SERVER_URL ?? ''}/Document/OpenFile/${props.document.documentID.toString()}`
    window.open(url, '_blank')
  }

  return <Grid container
    direction='row'
    sx={{
      justifyContent: 'space-between',
      alignItems: 'center'
    }}
  >
    <Grid item xs={6}>
      <LargeCard header={''} className='mt-5 card-button'
        content={
          <Grid container direction='row'
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Grid item xs zeroMinWidth>
              <Box style={{ overflowWrap: 'break-word' }}>{props.document.fileName}</Box>
              <Box>{FormatDateValue(props.document.whenUploaded)}</Box>
            </Grid>
            <Grid item xs='auto'>
              <Button onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                setShowVerify(!props.document.verified)
                setMenuAnchor(event.currentTarget)
              }}
              >
                <MoreVert />
              </Button>
            </Grid>
          </Grid>
        }
      />
    </Grid>
    <Grid item xs={6}>
      {
        props.document.verified && (
          <table>
            <tbody>
              <tr>
                <td>
                  <CheckCircleRounded className='mr-5' htmlColor='#02AED9' />
                </td>
                <td>
                  <div>Verified by {props.document.verifiedBy} </div>
                  <div>{FormatDateTimeValue(props.document.verifiedDate)} </div>
                  {props.document.caregivers?.map((value, i) => {
                    return <div>{value.caregiverName}</div>
                  })}
                  {props.document.incomeAmount !== null && <div data-testid='incomeValue'>Documented Income: {FormatCurrency(props.document.incomeAmount)}</div>}
                </td>
              </tr>
              <tr>
                <td colSpan={2} width='300px'>{props.document.verificationNote}</td>
              </tr>

            </tbody>
          </table>
        )
      }
    </Grid>
    <Menu
      open={menuAnchor !== null}
      onClose={() => { setMenuAnchor(null) }}
      anchorEl={menuAnchor}
    >
      {canOpen && <MenuItem key='Open' onClick={openDocument} >
        <OpenInBrowserRounded className='mr-5' /> Open
      </MenuItem>
      }
      {props.onDownload !== null && props.onDownload !== undefined && (
        <MenuItem key='Download' onClick={() => { props.onDownload !== undefined ? props.onDownload(props.document.documentID) : noReturn() }} >
          <DownloadForOfflineRounded className='mr-5' /> Download
        </MenuItem>
      )}
      {props.canVerify && showVerify && <MenuItem key='Verify' onClick={() => {
        props.onVerifyChanged(showVerify)
        setMenuAnchor(null)
      }}
      >
        <CheckCircleRounded className='mr-5' /> Verify
      </MenuItem>}
      {props.canVerify && !showVerify && <MenuItem key='Unverify' onClick={() => {
        props.onVerifyChanged(showVerify)
        setMenuAnchor(null)
      }}
      >
        <UndoRounded className='mr-5' /> Unverify
      </MenuItem>}
      {(props.canDelete ?? props.canEdit) && <MenuItem key='Delete' onClick={() => {
        props.onDelete()
        setMenuAnchor(null)
      }}
      >
        <DeleteOutlineRounded className='mr-5' /> {props.deleteOptionText ?? 'Delete'}
      </MenuItem>}
    </Menu>
  </Grid >
}
